//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { PinterestGatewayUser } from '@bvengineering/pinterest-gateway-service-types';

import {
  ContentLibrarySourcesGroups,
  MediaSourceParamKey,
} from '@apis/content-library/contentLibraryApiModel';
import { IConnectedBrandInstagramBusinessAccounts } from '@apis/instagram-settings/gatewayInstagramSettingsApiModel';
import { IInstagramAccount } from '@apis/instagram-settings/instagramSettingsApiModel';
import { IUserInfo } from '@apis/team-management/teamManagementApiModel';
import { ITrackedCompetitor } from '@instagram-account/redux/competitors/instagramCompetitorsModel';
import ITrackedHashtag from '@models/ITrackedHashtag';
import ITrackedUser from '@models/ITrackedUser';
import InstagramUserId from '@models/InstagramUserId';

export interface MediaLibrarySourcesConfigs {
  instagramConfigs: MediaLibrarySourcesInstagramConfigs;
  pinterestUser: PinterestGatewayUser | undefined;
  teamMembers: IUserInfo[];
}

export interface MediaLibrarySourcesInstagramConfigs {
  connectedInstagramAccounts:
    | IConnectedBrandInstagramBusinessAccounts[]
    | undefined;
  defaultInstagramAccount: IInstagramAccount | undefined;
  trackedBusinessUsers: ITrackedUser[];
  trackedCompetitors: ITrackedCompetitor[];
  trackedHashtags: ITrackedHashtag[];
  trackedUsers: ITrackedUser[];
}

export interface MediaLibrarySourcesFeatureFlags {
  isInstagramCompetitorsEnabled: boolean;
  isDamImportDropboxEnabled: boolean;
  isYouTubeEnabled: boolean;
  isMultiHandleFeatureEnabled: boolean;
}

interface MediaLibrarySource<T extends string | boolean> {
  paramKey: MediaSourceParamKey;
  value: T;
  label: string;
  isIndeterminate: boolean;
  isSelected: boolean;
  subItems?: MediaLibrarySourcesDropdownItemData[];
}

type MediaLibrarySourceGroup =
  (typeof ContentLibrarySourcesGroups)[keyof typeof ContentLibrarySourcesGroups];

export interface MediaLibrarySourcesDropdownItemGroup
  extends MediaLibrarySource<MediaLibrarySourceGroup> {
  paramKey: 'sourcesGroups';
  subItems: MediaLibrarySourcesDropdownItemData[];
  value: ContentLibrarySourcesGroups;
}

export interface MediaLibrarySourcesDropdownItemString
  extends MediaLibrarySource<string> {
  paramKey:
    | 'curalateUserIds'
    | 'accountMentionIds'
    | 'trackedBusinessUserIds'
    | 'trackedHashtags'
    | 'trackedUserIds'
    | 'pinterestUserIds';
}
export interface MediaLibrarySourcesDropdownItemBoolean
  extends MediaLibrarySource<boolean> {
  paramKey: 'brandFeedPost' | 'brandStoryPost';
}

export type MediaLibrarySourcesDropdownItemData =
  | MediaLibrarySourcesDropdownItemGroup
  | MediaLibrarySourcesDropdownItemString
  | MediaLibrarySourcesDropdownItemBoolean;

export type MediaLibraryModelSource = MediaLibrarySourcesDropdownItemGroup;

export const MaxDisplayItems: Record<ContentLibrarySourcesGroups, number> = {
  [ContentLibrarySourcesGroups.ALL_BRAND_POSTS]: Infinity,
  [ContentLibrarySourcesGroups.ALL_INSTAGRAM]: Infinity,
  [ContentLibrarySourcesGroups.ALL_TIKTOK]: Infinity,
  [ContentLibrarySourcesGroups.ALL_YOUTUBE]: Infinity,
  [ContentLibrarySourcesGroups.ALL_MEDIA]: Infinity,
  [ContentLibrarySourcesGroups.ALL_PINTEREST]: Infinity,
  [ContentLibrarySourcesGroups.ALL_TRACKED_COMPETITORS]: Infinity,
  [ContentLibrarySourcesGroups.ALL_TRACKED_HASHTAGS]: 10,
  [ContentLibrarySourcesGroups.ALL_TRACKED_USERS]: 10,
  [ContentLibrarySourcesGroups.TEAM_MEDIA]: 10,
  [ContentLibrarySourcesGroups.ALL_DROPBOX]: Infinity,
} as const;

export const DEFAULT_SOURCES_LABEL = 'Add or Remove Sources';

export const MediaLibrarySourceDisplayText = {
  [ContentLibrarySourcesGroups.ALL_MEDIA]: 'All Media',
  [ContentLibrarySourcesGroups.TEAM_MEDIA]: 'Team Media',
  [ContentLibrarySourcesGroups.ALL_TRACKED_COMPETITORS]: 'Tracked Competitors',
  [ContentLibrarySourcesGroups.ALL_TRACKED_HASHTAGS]: 'Tracked Hashtags',
  [ContentLibrarySourcesGroups.ALL_TRACKED_USERS]: 'Tracked Users',
  [ContentLibrarySourcesGroups.ALL_INSTAGRAM]: 'Instagram Media',
  [ContentLibrarySourcesGroups.ALL_PINTEREST]: 'Pinterest Media',
  [ContentLibrarySourcesGroups.ALL_BRAND_POSTS]: 'Instagram Account',
  [ContentLibrarySourcesGroups.ALL_DROPBOX]: 'Dropbox Media',
  [ContentLibrarySourcesGroups.ALL_TIKTOK]: 'TikTok Media',
  [ContentLibrarySourcesGroups.ALL_YOUTUBE]: 'YouTube Media',
  accountMentionIds: 'Mentioned & Tagged',
  brandFeedPost: 'Instagram Feed Posts',
  brandStoryPost: 'Instagram Story Posts',
  pinterestUserIds: 'Pinterest Account',
} as const;

export const MAX_DISPLAY_SOURCES = 3;
export const MAX_DISPLAY_SOURCES_SUMMARY_LENGTH = 35;
